'use client';

import React, { useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

interface ThumbnailPickerProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
}

export default function ThumbnailPicker({ onFileSelect, selectedFile }: ThumbnailPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
      const url = URL.createObjectURL(file);
      setPreview(url);
    }
  };

  const clearThumbnail = () => {
    onFileSelect(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  if (preview) {
    return (
      <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
        <AppImage
          src={preview}
          alt="Uploaded thumbnail preview for clip"
          fill
          className="object-cover"
          sizes="400px"
          unoptimized
        />
        <button
          type="button"
          onClick={clearThumbnail}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-lg transition-all hover:opacity-80"
          style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}
        >
          <X size={13} />
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-8 cursor-pointer transition-all duration-200 hover:border-primary"
      style={{ borderColor: 'var(--border)', background: 'var(--muted)', aspectRatio: '16/9' }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileInput}
      />
      <span
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
        style={{ background: 'rgba(108, 99, 255, 0.1)', border: '1px solid rgba(108, 99, 255, 0.25)' }}
      >
        <ImagePlus size={18} style={{ color: 'var(--primary)' }} />
      </span>
      <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--foreground)' }}>
        Upload Thumbnail
      </p>
      <p className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>
        JPG, PNG, WebP
      </p>
    </div>
  );
}